import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Bell,
  Phone,
  Flame,
  CalendarCheck,
  Video,
  MapPin,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import AppShell from "@/components/layout/AppShell";
import NewRdvButton from "@/components/crm/NewRdvButton";
import { cn } from "@/lib/utils";
import { leadsApi, rappelsApi, calendarApi } from "@/lib/crmApi";
import { useSettings } from "@/context/SettingsContext";
import {
  formatDate,
  isOverdue,
  isDueToday,
  bucketOf,
  BUCKET_META,
} from "@/lib/crmHelpers";

const MONTHS_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];
const DAYS_FR = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const ymd = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

function buildCalendarGrid(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  // Monday-first weekday
  const startOffset = (first.getDay() + 6) % 7;
  const totalDays = last.getDate();
  const cells = [];
  // Previous month tail
  for (let i = startOffset; i > 0; i--) {
    const d = new Date(year, month, 1 - i);
    cells.push({ date: d, currentMonth: false });
  }
  for (let i = 1; i <= totalDays; i++) {
    cells.push({ date: new Date(year, month, i), currentMonth: true });
  }
  // Next month head
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length - 1].date;
    const next = new Date(last);
    next.setDate(last.getDate() + 1);
    cells.push({ date: next, currentMonth: false });
  }
  // Always 6 rows for stable layout
  while (cells.length < 42) {
    const last = cells[cells.length - 1].date;
    const next = new Date(last);
    next.setDate(last.getDate() + 1);
    cells.push({ date: next, currentMonth: false });
  }
  return cells;
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const { activeProjectId } = useSettings();
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [leads, setLeads] = useState([]);
  const [rappels, setRappels] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Window: from start of previous month to end of next month — covers
      // the visible grid (which spans up to 6 weeks across two months).
      const start = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1).toISOString();
      const end = new Date(cursor.getFullYear(), cursor.getMonth() + 2, 0, 23, 59, 59).toISOString();
      const [l, r, ap] = await Promise.all([
        leadsApi.list(activeProjectId),
        rappelsApi.list(null, false, activeProjectId),
        calendarApi.appointments({ start, end, projetId: activeProjectId }),
      ]);
      setLeads(Array.isArray(l) ? l : []);
      setRappels(Array.isArray(r) ? r : []);
      setAppointments(Array.isArray(ap?.appointments) ? ap.appointments : []);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [activeProjectId, cursor]);

  useEffect(() => {
    load();
  }, [load]);

  const cells = useMemo(
    () => buildCalendarGrid(cursor.getFullYear(), cursor.getMonth()),
    [cursor]
  );

  // Aggregate events by ymd. RDV use the LOCAL date (Africa/Dakar) computed
  // server-side so the UI doesn't have to redo timezone math.
  const eventsByDay = useMemo(() => {
    const map = {};
    const ensure = (k) => {
      if (!map[k]) map[k] = { rappels: [], relances: [], rdvs: [] };
      return map[k];
    };
    leads.forEach((l) => {
      if (!l.prochaine_action) return;
      ensure(String(l.prochaine_action).slice(0, 10)).relances.push(l);
    });
    rappels.forEach((r) => {
      if (!r.date_rappel) return;
      ensure(String(r.date_rappel).slice(0, 10)).rappels.push(r);
    });
    appointments.forEach((a) => {
      if (!a.rdv_local_date) return;
      ensure(a.rdv_local_date).rdvs.push(a);
    });
    // Sort RDVs of each day by hour
    Object.values(map).forEach((d) => {
      d.rdvs.sort((a, b) => (a.rdv_local_time || "").localeCompare(b.rdv_local_time || ""));
    });
    return map;
  }, [leads, rappels, appointments]);

  // Total RDV for the visible month (used in the stats strip)
  const rdvsThisMonth = useMemo(() => {
    return appointments.filter((a) => {
      if (!a.rdv_local_date) return false;
      const [y, m] = a.rdv_local_date.split("-").map(Number);
      return y === cursor.getFullYear() && m === cursor.getMonth() + 1;
    });
  }, [appointments, cursor]);

  const goToday = () => setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
  const prevMonth = () =>
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const nextMonth = () =>
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));

  const upcomingThisMonth = useMemo(() => {
    const monthLeads = leads.filter((l) => {
      if (!l.prochaine_action) return false;
      const d = new Date(l.prochaine_action);
      return d.getFullYear() === cursor.getFullYear() && d.getMonth() === cursor.getMonth();
    });
    return monthLeads.length;
  }, [leads, cursor]);

  const upcomingRappels = useMemo(() => {
    return rappels.filter((r) => {
      if (!r.date_rappel) return false;
      const d = new Date(r.date_rappel);
      return d.getFullYear() === cursor.getFullYear() && d.getMonth() === cursor.getMonth();
    }).length;
  }, [rappels, cursor]);

  const selectedDayEvents = selectedDate
    ? eventsByDay[ymd(selectedDate)] || { rappels: [], relances: [] }
    : null;

  return (
    <AppShell topbarProps={{ showSearch: false, showAdd: false, onRefresh: load }}>
      <div className="mx-auto max-w-[1400px] px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
        {/* Page header */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-label text-muted-foreground">Planning</p>
            <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">
              Calendrier
            </h1>
            <p className="text-sm text-muted-foreground">
              Vos rappels et relances du mois en un coup d'œil.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <NewRdvButton leads={leads} onCreated={load} />
            <div className="mx-1 h-6 w-px bg-border/60" />
            <Button variant="outline" size="sm" onClick={prevMonth} className="touch-target" aria-label="Mois précédent">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={goToday}
              data-testid="cal-today-btn"
              className="font-medium"
            >
              Aujourd'hui
            </Button>
            <Button variant="outline" size="sm" onClick={nextMonth} className="touch-target" aria-label="Mois suivant">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Stats strip */}
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
          <div className="rounded-xl border border-border/60 bg-card p-3">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
              <CalendarDays className="h-3.5 w-3.5" />
              Mois
            </div>
            <div className="font-heading mt-1 text-base font-bold">
              {MONTHS_FR[cursor.getMonth()]} {cursor.getFullYear()}
            </div>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 dark:border-emerald-900/40 dark:bg-emerald-950/20">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
              <CalendarCheck className="h-3.5 w-3.5" />
              RDV
            </div>
            <div className="font-heading mt-1 text-base font-bold text-emerald-700 dark:text-emerald-300">
              {rdvsThisMonth.length}
              {rdvsThisMonth.some((r) => r.has_conflict) && (
                <span title="Conflits détectés" className="ml-1 inline-flex items-center text-amber-600">
                  <AlertTriangle className="h-3.5 w-3.5" />
                </span>
              )}
            </div>
          </div>
          <div className="rounded-xl border border-border/60 bg-card p-3">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
              <Phone className="h-3.5 w-3.5" />
              Relances
            </div>
            <div className="font-heading mt-1 text-base font-bold">{upcomingThisMonth}</div>
          </div>
          <div className="rounded-xl border border-border/60 bg-card p-3">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
              <Bell className="h-3.5 w-3.5" />
              Rappels
            </div>
            <div className="font-heading mt-1 text-base font-bold">{upcomingRappels}</div>
          </div>
          <div className="rounded-xl border border-border/60 bg-card p-3">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
              <Flame className="h-3.5 w-3.5 text-red-500" />
              En retard
            </div>
            <div className="font-heading mt-1 text-base font-bold text-red-600">
              {leads.filter((l) => isOverdue(l.prochaine_action)).length}
            </div>
          </div>
        </div>

        {/* Calendar grid */}
        <div
          className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-soft"
          data-testid="calendar-grid"
        >
          {/* Day headers */}
          <div className="grid grid-cols-7 border-b border-border/60 bg-muted/30">
            {DAYS_FR.map((d) => (
              <div
                key={d}
                className="px-2 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-muted-foreground sm:text-xs"
              >
                <span className="hidden sm:inline">{d}</span>
                <span className="sm:hidden">{d[0]}</span>
              </div>
            ))}
          </div>

          {/* Cells */}
          <div className="grid grid-cols-7">
            {cells.map((cell, i) => {
              const key = ymd(cell.date);
              const ev = eventsByDay[key];
              const isToday = isSameDay(cell.date, today);
              const rdvs = ev?.rdvs || [];
              const totalEvents =
                (ev?.rappels.length || 0) + (ev?.relances.length || 0) + rdvs.length;
              const hasOverdue = ev?.relances.some((l) => isOverdue(l.prochaine_action));
              const hasConflict = rdvs.some((r) => r.has_conflict);

              return (
                <button
                  key={i}
                  onClick={() => setSelectedDate(cell.date)}
                  data-testid={`cal-cell-${key}`}
                  className={cn(
                    "group relative flex min-h-[64px] flex-col items-stretch border-b border-r border-border/40 p-1.5 text-left transition-colors hover:bg-muted/40 sm:min-h-[110px] sm:p-2",
                    !cell.currentMonth && "bg-muted/20 text-muted-foreground/50",
                    (i + 1) % 7 === 0 && "border-r-0",
                    i >= cells.length - 7 && "border-b-0"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={cn(
                        "inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold sm:text-sm",
                        isToday && "bg-accent-navy text-white dark:bg-primary dark:text-primary-foreground"
                      )}
                    >
                      {cell.date.getDate()}
                    </span>
                    {totalEvents > 0 && (
                      <span
                        className={cn(
                          "hidden h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] font-bold sm:inline-flex",
                          hasConflict
                            ? "bg-amber-500 text-white"
                            : rdvs.length > 0
                            ? "bg-emerald-500 text-white"
                            : hasOverdue
                            ? "bg-red-500 text-white"
                            : "bg-accent-navy/10 text-accent-navy dark:bg-accent-gold/20 dark:text-accent-gold"
                        )}
                        title={hasConflict ? "Conflit de RDV" : undefined}
                      >
                        {totalEvents}
                      </span>
                    )}
                  </div>

                  {/* Mobile: just dots */}
                  {totalEvents > 0 && (
                    <div className="mt-auto flex items-center gap-0.5 sm:hidden">
                      {rdvs.slice(0, 3).map((_, idx) => (
                        <span
                          key={`d${idx}`}
                          className={cn(
                            "h-1 w-1 rounded-full",
                            hasConflict ? "bg-amber-500" : "bg-emerald-500"
                          )}
                        />
                      ))}
                      {ev?.relances.slice(0, 2).map((_, idx) => (
                        <span
                          key={`r${idx}`}
                          className={cn(
                            "h-1 w-1 rounded-full",
                            hasOverdue ? "bg-red-500" : "bg-accent-navy dark:bg-accent-gold"
                          )}
                        />
                      ))}
                      {ev?.rappels.slice(0, 1).map((_, idx) => (
                        <span key={`b${idx}`} className="h-1 w-1 rounded-full bg-accent-blue" />
                      ))}
                    </div>
                  )}

                  {/* Desktop: event chips */}
                  <div className="mt-1 hidden flex-1 flex-col gap-0.5 sm:flex">
                    {/* RDVs first — they're the most actionable signal */}
                    {rdvs.slice(0, 2).map((r) => (
                      <span
                        key={`d-${r.id}`}
                        className={cn(
                          "flex items-center gap-1 truncate rounded px-1.5 py-0.5 text-[10px] font-medium",
                          r.has_conflict
                            ? "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200 ring-1 ring-amber-400"
                            : "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                        )}
                        title={`${r.rdv_local_time} — ${r.nom}${r.has_conflict ? " ⚠️ conflit" : ""}`}
                      >
                        {r.rdv_kind === "visio" ? (
                          <Video className="h-2.5 w-2.5 flex-none" />
                        ) : (
                          <MapPin className="h-2.5 w-2.5 flex-none" />
                        )}
                        <span className="font-bold tabular-nums">{r.rdv_local_time}</span>
                        <span className="truncate">{r.nom}</span>
                      </span>
                    ))}
                    {ev?.relances.slice(0, 2).map((l) => {
                      const overdue = isOverdue(l.prochaine_action);
                      const today = isDueToday(l.prochaine_action);
                      return (
                        <span
                          key={`l-${l.id}`}
                          className={cn(
                            "truncate rounded px-1.5 py-0.5 text-[10px] font-medium",
                            overdue
                              ? "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300"
                              : today
                              ? "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                              : "bg-accent-navy/10 text-accent-navy dark:bg-accent-navy/30 dark:text-blue-300"
                          )}
                          title={`${l.nom} — relance`}
                        >
                          📞 {l.nom}
                        </span>
                      );
                    })}
                    {ev?.rappels.slice(0, 2).map((r) => (
                      <span
                        key={`r-${r.id}`}
                        className="truncate rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"
                        title={r.commentaire || r.canal}
                      >
                        🔔 {r.commentaire?.slice(0, 18) || r.canal || "Rappel"}
                      </span>
                    ))}
                    {totalEvents > 4 && (
                      <span className="text-[10px] text-muted-foreground">
                        +{totalEvents - 4} autres
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {loading && (
          <p className="mt-4 text-center text-xs text-muted-foreground">Chargement...</p>
        )}
      </div>

      {/* Day details Sheet */}
      <Sheet open={!!selectedDate} onOpenChange={(o) => !o && setSelectedDate(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-left">
              <CalendarDays className="h-5 w-5 text-accent-navy" />
              {selectedDate && formatDate(selectedDate)}
            </SheetTitle>
          </SheetHeader>

          <div className="mt-5 space-y-5 overflow-y-auto pr-1" style={{ maxHeight: "calc(100vh - 100px)" }}>
            {/* RDV — affichés en premier */}
            <section>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5">
                  <CalendarCheck className="h-3.5 w-3.5 text-emerald-600" />
                  Rendez-vous ({selectedDayEvents?.rdvs.length || 0})
                </h3>
              </div>
              {(!selectedDayEvents?.rdvs || selectedDayEvents.rdvs.length === 0) ? (
                <p className="rounded-lg border border-dashed border-border/60 p-4 text-center text-xs text-muted-foreground">
                  Aucun rendez-vous ce jour.
                </p>
              ) : (
                <ul className="space-y-2">
                  {selectedDayEvents.rdvs.map((r) => (
                    <li
                      key={`d-${r.id}`}
                      onClick={() => navigate(`/leads/${r.id}`)}
                      data-testid={`cal-event-rdv-${r.id}`}
                      className={cn(
                        "cursor-pointer rounded-lg border p-3 transition-colors hover:bg-muted/40",
                        r.has_conflict
                          ? "border-amber-300 bg-amber-50/50 dark:border-amber-800/60 dark:bg-amber-950/20"
                          : "border-border/60 bg-card"
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-heading rounded bg-emerald-600 px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-white">
                              {r.rdv_local_time || "--:--"}
                            </span>
                            <span className="font-heading truncate text-sm font-semibold">{r.nom}</span>
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                            <span className="inline-flex items-center gap-1">
                              {r.rdv_kind === "visio" ? (
                                <><Video className="h-3 w-3" /> Visio</>
                              ) : (
                                <><MapPin className="h-3 w-3" /> Showroom Dakar</>
                              )}
                            </span>
                            {r.telephone && <span>{r.telephone}</span>}
                            {r.programme_immobilier && <span>· {r.programme_immobilier}</span>}
                          </div>
                          {r.has_conflict && (
                            <div className="mt-1.5 inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                              <AlertTriangle className="h-3 w-3" />
                              Conflit avec un autre RDV à la même heure
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Relances */}
            <section>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5" />
                  Relances ({selectedDayEvents?.relances.length || 0})
                </h3>
              </div>
              {selectedDayEvents?.relances.length === 0 ? (
                <p className="rounded-lg border border-dashed border-border/60 p-4 text-center text-xs text-muted-foreground">
                  Aucune relance programmée.
                </p>
              ) : (
                <ul className="space-y-2">
                  {selectedDayEvents?.relances.map((l) => {
                    const b = BUCKET_META[bucketOf(l)];
                    return (
                      <li
                        key={l.id}
                        onClick={() => navigate(`/leads/${l.id}`)}
                        data-testid={`cal-event-lead-${l.id}`}
                        className="cursor-pointer rounded-lg border border-border/60 bg-card p-3 transition-colors hover:bg-muted/40"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-heading truncate text-sm font-semibold">{l.nom}</div>
                            <div className="truncate text-xs text-muted-foreground">{l.telephone}</div>
                          </div>
                          <span
                            className={`flex-none rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase ${b.color}`}
                          >
                            {b.label}
                          </span>
                        </div>
                        {l.notes && (
                          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{l.notes}</p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {/* Rappels */}
            <section>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5">
                  <Bell className="h-3.5 w-3.5" />
                  Rappels ({selectedDayEvents?.rappels.length || 0})
                </h3>
              </div>
              {selectedDayEvents?.rappels.length === 0 ? (
                <p className="rounded-lg border border-dashed border-border/60 p-4 text-center text-xs text-muted-foreground">
                  Aucun rappel ce jour.
                </p>
              ) : (
                <ul className="space-y-2">
                  {selectedDayEvents?.rappels.map((r) => (
                    <li
                      key={r.id}
                      onClick={() => r.lead_id && navigate(`/leads/${r.lead_id}`)}
                      data-testid={`cal-event-rappel-${r.id}`}
                      className="cursor-pointer rounded-lg border border-border/60 bg-card p-3 transition-colors hover:bg-muted/40"
                    >
                      <div className="flex items-center gap-2">
                        <Bell className="h-3.5 w-3.5 flex-none text-accent-blue" />
                        <span className="font-heading text-sm font-semibold">
                          {r.canal ? r.canal.toUpperCase() : "Rappel"}
                        </span>
                      </div>
                      {r.commentaire && (
                        <p className="mt-1 text-xs text-muted-foreground">{r.commentaire}</p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </SheetContent>
      </Sheet>
    </AppShell>
  );
}
